import Alpine from 'alpinejs';
import { getCsrfToken } from '../../utils/helpers.js';

Alpine.store('wishlistModal', {
    isOpen: false,
    listingId: null,
    pendingWishlists: [], // Array of UIDs
    wishlists: [], // Loaded from API or preloaded from user fetch
    newCollectionName: '',
    isSaving: false,
    isLoading: false,
    isLoaded: false, // Set by user.js after preloading

    get creatingNewList() {
        return this.newCollectionName.trim().length > 0;
    },

    init() {
        // Legacy: Initial load of wishlists if available from window
        if (window.avari && window.avari.wishlists) {
            this.setWishlists(window.avari.wishlists);
            this.isLoaded = true;
        }
    },

    setWishlists(wishlists) {
        const lists = Array.isArray(wishlists) ? wishlists : [];
        this.wishlists = lists.map((list) => ({
            ...list,
            covers: Array.isArray(list?.covers)
                ? list.covers.filter(Boolean)
                : [],
            listingIds: Array.isArray(list?.listingIds) ? list.listingIds : []
        }));
    },

    open(listingId) {
        this.listingId = listingId;
        this.newCollectionName = '';

        // If already loaded, just set pending wishlists
        if (this.isLoaded && this.wishlists.length >= 0) {
            this.pendingWishlists = this.wishlists
                .filter((list) =>
                    list.listingIds.includes(parseInt(this.listingId))
                )
                .map((list) => list.uid);
        } else {
            // Fetch if not preloaded
            this.loadWishlists();
        }

        // Open the actual dialog via modals store
        if (Alpine.store('modals')) {
            Alpine.store('modals').open('add-to-wishlist-modal');
        }
    },

    async loadWishlists() {
        if (this.isLoading) return;
        this.isLoading = true;

        try {
            const response = await fetch('/actions/avari/wishlist/index', {
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    Accept: 'application/json'
                },
                credentials: 'same-origin'
            });
            const data = await response.json();
            if (data.success) {
                this.setWishlists(data.wishlists);
                this.isLoaded = true;

                // Determine which lists currently have this property
                this.pendingWishlists = this.wishlists
                    .filter((list) =>
                        list.listingIds.includes(parseInt(this.listingId))
                    )
                    .map((list) => list.uid);
            }
        } catch (e) {
            console.error('Failed to load wishlists', e);
        } finally {
            this.isLoading = false;
        }
    },

    toggleList(uid) {
        if (this.pendingWishlists.includes(uid)) {
            this.pendingWishlists = this.pendingWishlists.filter(
                (id) => id !== uid
            );
        } else {
            this.pendingWishlists.push(uid);
        }
    },

    async save() {
        if (!this.listingId || this.isSaving) return;
        this.isSaving = true;

        const csrf = await getCsrfToken();
        if (!csrf) {
            console.error('CSRF token not available');
            return;
        }

        const formData = new FormData();
        formData.append('listingId', this.listingId);
        formData.append(csrf.name, csrf.value);

        // Append selected wishlists
        // Note: Sending as array requires recursive appending or comma string if backend handles it
        // Our controller handles comma separated string for robustness
        formData.append('wishlistUids', this.pendingWishlists.join(','));

        if (this.newCollectionName.trim()) {
            formData.append('newWishlistTitle', this.newCollectionName.trim());
        }

        try {
            const response = await fetch('/actions/avari/wishlist/update', {
                method: 'POST',
                body: formData,
                headers: {
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                credentials: 'same-origin'
            });

            const data = await response.json();

            if (data.success) {
                // Update global wishlist state (red hearts)
                if (
                    Alpine.store('wishlist') &&
                    Array.isArray(data.allDetails)
                ) {
                    // Update the global list of liked IDs based on the response
                    Alpine.store('wishlist').items = data.allDetails.map((id) =>
                        parseInt(id)
                    );
                }

                // Refresh wishlists cache (new collections may have been created)
                this.isLoaded = false;
                this.loadWishlists();

                if (Alpine.store('modals')) {
                    // Close using element to ensure compatibility
                    const modal = document.getElementById(
                        'add-to-wishlist-modal'
                    );
                    if (modal) {
                        Alpine.store('modals').close(modal);
                    } else {
                        // Fallback to string ID
                        Alpine.store('modals').close('add-to-wishlist-modal');
                    }
                }

                // Clear state
                this.newCollectionName = '';
            } else {
                console.error('Failed to save wishlist', data.error);
                if (Alpine.store('modals')) {
                    Alpine.store('modals').markAsError('add-to-wishlist-modal');
                }
            }
        } catch (e) {
            console.error('Error saving wishlist', e);
            if (Alpine.store('modals')) {
                Alpine.store('modals').markAsError('add-to-wishlist-modal');
            }
        } finally {
            this.isSaving = false;
        }
    }
});
